import type { ColorScheme } from "@/lib/color-scheme/scheme"

export const BRIDGE_PROTOCOL_VERSION = 1

export const BRIDGE_MESSAGE_TYPES = [
  "ready",
  "props",
  "visit",
  "visit.allow",
  "visit.hint",
  "page.meta",
  "screen.presentation",
  "browse.state",
  "browse.command",

  "bridge.click",
  "screen.visibility",
  "tab.reselected",
  "state.invalidate",
  "session.revoked",
  "session.probe",
  "session.probe.result",
  "error",
  "analytics.track",
  "checkout.completed",
  "checkout.entered",
  "checkout.start",
  "checkout.start.result",
  "idv.start",
  "idv.completed",
  "app.locked",
  "app.unlocked",
  "rum.session",
  "impressions.track",
  "share.open",
  "share.open.result",
  "share.story",
  "share.story.result",
  "share.channel",
  "share.channel.result",
] as const

export type BridgeMessageType = (typeof BRIDGE_MESSAGE_TYPES)[number]

export type BridgeErrorCode = "unknown_type" | "unsupported_version" | "invalid_data" | "origin_rejected" | "capability_unavailable" | "cancelled" | "failed"

export type BridgeDirection = "web_to_native" | "native_to_web" | "bidirectional"

export type SessionRevokedReason = "identity-mismatch" | "signed-out"

export const BRIDGE_MESSAGE_DIRECTIONS: Readonly<Record<BridgeMessageType, BridgeDirection>> = {
  ready: "web_to_native",
  props: "native_to_web",
  visit: "web_to_native",
  "visit.allow": "native_to_web",
  "visit.hint": "web_to_native",
  "page.meta": "web_to_native",
  "screen.presentation": "native_to_web",
  "browse.state": "web_to_native",
  "browse.command": "native_to_web",

  "bridge.click": "native_to_web",
  "screen.visibility": "native_to_web",
  "tab.reselected": "native_to_web",
  "state.invalidate": "web_to_native",
  "session.revoked": "native_to_web",
  "session.probe": "native_to_web",
  "session.probe.result": "web_to_native",
  error: "bidirectional",
  "analytics.track": "web_to_native",
  "checkout.completed": "web_to_native",
  "checkout.entered": "web_to_native",
  "checkout.start": "web_to_native",
  "checkout.start.result": "native_to_web",
  "idv.start": "web_to_native",
  "idv.completed": "native_to_web",
  "app.locked": "native_to_web",
  "app.unlocked": "native_to_web",
  "rum.session": "native_to_web",
  "impressions.track": "web_to_native",
  "share.open": "web_to_native",
  "share.open.result": "native_to_web",
  "share.story": "web_to_native",
  "share.story.result": "native_to_web",
  "share.channel": "web_to_native",
  "share.channel.result": "native_to_web",
}

export interface BridgeError {
  readonly code: BridgeErrorCode
  readonly message: string
}

interface BridgeEnvelopeBase<T extends BridgeMessageType> {
  readonly v: typeof BRIDGE_PROTOCOL_VERSION
  readonly type: T
  readonly id?: string
}

export interface BridgeDataEnvelope<T extends BridgeMessageType, D extends object = Record<string, unknown>> extends BridgeEnvelopeBase<T> {
  readonly data: D
  readonly error?: never
}

export interface BridgeErrorEnvelope<T extends BridgeMessageType> extends BridgeEnvelopeBase<T> {
  readonly data?: never
  readonly error: BridgeError
}

export interface BridgeNotificationEnvelope<T extends BridgeMessageType, D extends object = Record<string, unknown>> extends BridgeEnvelopeBase<T> {
  readonly data?: D
  readonly error?: never
}

export type BridgeResultMessageType = Extract<BridgeMessageType, `${string}.result`>

export type BridgeEnvelope<T extends BridgeMessageType = BridgeMessageType, D extends object = Record<string, unknown>> = T extends BridgeResultMessageType
  ? (BridgeDataEnvelope<T, D> | BridgeErrorEnvelope<T>) & { readonly id: string }
  : T extends "error"
    ? BridgeErrorEnvelope<T>
    : BridgeNotificationEnvelope<T, D>

export interface ReadyData {
  readonly url: string
  readonly capabilities: ReadonlyArray<string>
  readonly protocol: typeof BRIDGE_PROTOCOL_VERSION
}

export type VisitAction = "advance" | "replace" | "recede" | "refresh"

export const VISIT_DECISIONS = ["in-page", "push", "replace", "sheet", "native", "external", "refused"] as const

export type VisitDecision = (typeof VISIT_DECISIONS)[number]

export type BarButtonIcon = "cart" | "search" | "share" | "heart" | "filter" | "more"

export type BarButtonPlacement = "leading" | "trailing"

export type BridgeLocale = "en" | "es"

export interface VisitData {
  readonly url: string
  readonly action: VisitAction
  readonly title?: string
}

export interface VisitAllowData {
  readonly url: string
  readonly decision: VisitDecision
}

export interface VisitHintData {
  readonly url: string
}

export interface BarButtonData {
  readonly id: string
  readonly label: string
  readonly icon?: BarButtonIcon
  readonly placement?: BarButtonPlacement
  readonly badge?: number
}

export interface PageMetaData {
  readonly url: string
  readonly title?: string
  readonly subtitle?: string
  readonly scrollY?: number
  readonly bar?: ReadonlyArray<BarButtonData>
}

export interface BridgeClickData {
  readonly id: string
}

export interface ScreenVisibilityData {
  readonly visible: boolean
}

export interface StateInvalidateData {
  readonly keys: ReadonlyArray<string>
}

export interface CheckoutStartData {
  readonly variantId: string
  readonly trackingId: string
}

export interface CheckoutCompletedData {
  readonly loanUuid: string
  readonly displayId: string
}

export type StoreWebForwardedEvent = "MARKETPLACE_HOME_SCREEN_V2" | "VIEW_ITEM" | "VIEW_ITEM_LIST" | "SELECT_ITEM" | "BEGIN_CHECKOUT" | "PURCHASE" | "checkout_plan_selected"

export interface AnalyticsTrackData {
  readonly name: StoreWebForwardedEvent
  readonly params: Record<string, unknown>
}

export interface RumSessionData {
  readonly sessionId: string
}

export interface CheckoutEnteredData {
  readonly url: string
}

export interface IdvStartData {
  readonly cartId: string
}

export type IdvCompletedStatus = "verified" | "review_needed" | "rejected"

export interface IdvCompletedData {
  readonly cartId: string
  readonly status: IdvCompletedStatus
}

export interface CheckoutStartResultData {
  readonly cartId: string
}

export type ShareOutcome = "shared" | "dismissed"

export interface ShareOpenData {
  readonly message: string
}

export interface ShareOpenResultData {
  readonly outcome: ShareOutcome
}

export interface ShareStoryData {
  readonly code: string
  readonly shareUrl: string
  readonly imageUrls: readonly string[]
}

export type ShareChannel = "whatsapp" | "instagram_direct" | "facebook"

export interface ShareChannelData {
  readonly channel: ShareChannel
  readonly message: string
  readonly url: string
}

export interface SafeAreaInsets {
  readonly top: number
  readonly right: number
  readonly bottom: number
  readonly left: number
}

export interface PropsData {
  readonly safeArea: SafeAreaInsets
  readonly locale?: BridgeLocale
  readonly colorScheme?: ColorScheme
}

export type SessionProbeResultData = { readonly status: "authenticated"; readonly userUuid: string } | { readonly status: "anonymous" }

export type ScreenPresentation = "active" | "covered" | "hidden" | "suspended"

export interface ScreenPresentationData {
  readonly presentation: ScreenPresentation
}

export interface BrowseSnapshotData {
  readonly scrollY: number
  readonly anchor?: { readonly selector: string; readonly offset: number }
  readonly values: ReadonlyArray<{ readonly key: string; readonly value: number }>
  readonly rails: ReadonlyArray<{ readonly key: string; readonly left: number }>
}

export interface BrowseStateData extends Pick<PageMetaData, "url" | "title"> {
  readonly entryId: string
  readonly revision: number
  readonly canGoBack: boolean
  readonly snapshot?: BrowseSnapshotData
}

export interface BrowseCommandData {
  readonly action: "back" | "navigate" | "root" | "restore"
  readonly entryId: string
  readonly revision: number
  readonly url?: string
  readonly snapshot?: BrowseSnapshotData
}

export interface BridgeDataByType {
  readonly "impressions.track": { readonly type: "impression" | "click"; readonly serve_id: string; readonly occurred_at: string }
  readonly "screen.presentation": ScreenPresentationData
  readonly "browse.state": BrowseStateData
  readonly "browse.command": BrowseCommandData
  readonly "session.probe": { readonly handoff?: string }
  readonly "session.probe.result": SessionProbeResultData
  readonly "session.revoked": { readonly reason?: SessionRevokedReason }
  readonly ready: ReadyData
  readonly visit: VisitData
  readonly "state.invalidate": StateInvalidateData
  readonly "checkout.completed": CheckoutCompletedData
  readonly "checkout.entered": CheckoutEnteredData
  readonly "analytics.track": AnalyticsTrackData
  readonly "rum.session": RumSessionData
  readonly "idv.start": IdvStartData
  readonly "idv.completed": IdvCompletedData
  readonly "checkout.start": CheckoutStartData
  readonly "checkout.start.result": CheckoutStartResultData
  readonly "share.open": ShareOpenData
  readonly "share.open.result": ShareOpenResultData
  readonly "share.story": ShareStoryData
  readonly "share.story.result": ShareOpenResultData
  readonly "share.channel": ShareChannelData
  readonly "share.channel.result": ShareOpenResultData
  readonly props: PropsData
  readonly "page.meta": PageMetaData
  readonly "visit.hint": VisitHintData
  readonly "bridge.click": BridgeClickData
  readonly "visit.allow": VisitAllowData
  readonly "screen.visibility": ScreenVisibilityData
}

export type BridgeTypedPayloadType =
  | "impressions.track"
  | "screen.presentation"
  | "browse.state"
  | "browse.command"
  | "session.probe"
  | "session.probe.result"
  | "session.revoked"
  | "ready"
  | "visit"
  | "state.invalidate"
  | "checkout.completed"
  | "checkout.entered"
  | "analytics.track"
  | "rum.session"
  | "idv.start"
  | "idv.completed"
  | "checkout.start"
  | "checkout.start.result"
  | "share.open"
  | "share.open.result"
  | "share.story"
  | "share.story.result"
  | "share.channel"
  | "share.channel.result"
  | "props"
  | "page.meta"
  | "visit.hint"
  | "bridge.click"
  | "visit.allow"
  | "screen.visibility"
